/**
 * Wiki — knowledge base UI.
 * Only shared Design System components (see /core/design).
 * Behavior: list, filter, upload, edit, delete, download.
 */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  BookOpen,
  Brain,
  Check,
  Clock,
  Download,
  FileText,
  Lightbulb,
  Loader2,
  Pencil,
  Save,
  StickyNote,
  Tag,
  Trash2,
  Upload,
  Users,
} from 'lucide-react';
import { wikiApi } from '../lib/api';
import { mcpCall } from '../lib/mcp';
import { downloadBase64 } from '../lib/download';
import { normalizeLang } from '../lib/i18n';
import type { WikiEntry } from '../types';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  Input,
  Label,
  Modal,
  PageHeader,
  SearchInput,
  Textarea,
} from '../components/ui';
import { RefreshButton } from '../components/ui/RefreshButton';
import i18n from '../lib/i18n';

interface ProductDocPage {
  source: 'customer' | 'wiki';
  id: string;
  kind: string;
  status: string;
  editions: string[];
  locale: string;
  title: string;
  summary: string;
  content?: string;
}

interface ProductDocCatalog {
  ok: boolean;
  edition?: string | null;
  actual_edition?: string | null;
  pages: ProductDocPage[];
}

function productDocEntry(page: ProductDocPage): WikiEntry {
  return {
    id: `product:${page.id}`,
    title: page.title,
    module: page.source === 'customer' ? 'onboarding' : 'production-wiki',
    tags: [page.kind, page.status, page.locale],
    editions: page.editions,
    lifecycle: page.status === 'stable' || page.status === 'beta' ? 'active' : 'draft',
    problem: page.summary,
    solution: page.content ?? '',
    context: i18n.t('wiki.verzovanaProdukcniDokumentaceAcm', 'Verzovaná produkční dokumentace ACM'),
    author: 'ACM',
    created_at: '',
    problem_preview: page.summary,
  } as WikiEntry;
}

// ── Upload modal ──────────────────────────────────────────────────────────────

function UploadModal(
  { open, onClose, onUploaded }: {
    open: boolean;
    onClose: () => void;
    onUploaded: (id: string) => void;
  },
) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [id, setId] = useState('');
  const [module_, setModule] = useState('');
  const [tags, setTags] = useState('');
  const [status, setStatus] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<
    { id: string; title: string; lines: number; size: number } | null
  >(null);
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setId('');
    setModule('');
    setTags('');
    setStatus('idle');
    setResult(null);
    setErrorMsg('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) {
      setFile(f);
      if (!id) {
        setId(f.name.replace(/\.[^/.]+$/, '').replace(/\s+/g, '-').toLowerCase().slice(0, 60));
      }
    }
  }, [id]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    if (f) {
      setFile(f);
      if (!id) {
        setId(f.name.replace(/\.[^/.]+$/, '').replace(/\s+/g, '-').toLowerCase().slice(0, 60));
      }
    }
  };

  const upload = async () => {
    if (!file) return;
    setStatus('uploading');
    try {
      const res = await wikiApi.upload(file, {
        id: id || undefined,
        module: module_ || undefined,
        tags: tags || undefined,
      });
      setResult(res);
      setStatus('done');
    } catch (e) {
      setErrorMsg((e as Error).message);
      setStatus('error');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <span className='inline-flex items-center gap-2'>
          <Upload size={15} className='text-blue-400' />
          {t('wiki.uploadModalTitle', 'Nahrát soubor do Wiki')}
        </span>
      }
      size='md'
      footer={status === 'done' && result
        ? (
          <>
            <Button variant='primary' onClick={() => onUploaded(result.id)}>
              {t('wiki.openButton', 'Otevřít')}
            </Button>
            <Button variant='secondary' onClick={handleClose}>
              {t('wiki.closeButton', 'Zavřít')}
            </Button>
          </>
        )
        : (
          <>
            <Button
              variant='primary'
              onClick={upload}
              disabled={!file || status === 'uploading'}
              loading={status === 'uploading'}
              icon={<Upload size={14} />}
            >
              {status === 'uploading'
                ? t('wiki.uploadingLabel', 'Nahrávám…')
                : t('wiki.uploadSubmit', 'Nahrát')}
            </Button>
            <Button variant='secondary' onClick={handleClose}>
              {t('wiki.cancel', 'Zrušit')}
            </Button>
          </>
        )}
    >
      {status === 'done' && result
        ? (
          <Alert variant='success'>
            <AlertTitle className='flex items-center gap-2'>
              <Check size={14} /> {t('wiki.uploadSuccess', 'Nahráno úspěšně')}
            </AlertTitle>
            <AlertDescription className='mt-2 space-y-1 text-xs'>
              <div>
                <span className='text-gray-500'>{t('wiki.idLabel', 'ID:')}</span> {result.id}
              </div>
              <div>
                <span className='text-gray-500'>{t('wiki.uploadResultTitleLabel', 'Titulek:')}</span>{' '}
                {result.title}
              </div>
              <div>
                <span className='text-gray-500'>{t('wiki.sizeLabel', 'Velikost:')}</span>{' '}
                {t('wiki.uploadResultSizeInfo', '{{size}} KB · {{lines}} řádků', {
                  size: Math.round(result.size / 1024),
                  lines: result.lines.toLocaleString(),
                })}
              </div>
            </AlertDescription>
          </Alert>
        )
        : (
          <div className='space-y-4'>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className={[
                'cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors',
                dragging
                  ? 'border-blue-500 bg-blue-600/10'
                  : file
                  ? 'border-emerald-600/40 bg-emerald-600/5'
                  : 'border-gray-700 bg-gray-900/20 hover:border-gray-600',
              ].join(' ')}
            >
              <input
                ref={inputRef}
                type='file'
                accept='.md,.json,.txt'
                className='hidden'
                onChange={onFileChange}
              />
              {file
                ? (
                  <div className='space-y-1'>
                    <FileText size={22} className='mx-auto text-emerald-400' />
                    <p className='text-sm font-medium text-white'>{file.name}</p>
                    <p className='text-xs text-gray-500'>{Math.round(file.size / 1024)} KB</p>
                  </div>
                )
                : (
                  <div className='space-y-2'>
                    <Upload size={22} className='mx-auto text-gray-600' />
                    <p className='text-sm text-gray-400'>
                      {t('wiki.dropzoneHint', 'Přetáhni soubor nebo klikni')}
                    </p>
                    <p className='text-xs text-gray-600'>.md · .json · .txt</p>
                  </div>
                )}
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label>{t('wiki.idOptionalLabel', 'ID (volitelné)')}</Label>
                <Input
                  size='sm'
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  placeholder={t('wiki.idPlaceholder', 'auto ze jména souboru')}
                />
              </div>
              <div>
                <Label>{t('wiki.moduleLabel', 'Modul')}</Label>
                <Input
                  size='sm'
                  value={module_}
                  onChange={(e) => setModule(e.target.value)}
                  placeholder={t('wiki.modulePlaceholderUpload', 'fleet, mqtt, …')}
                />
              </div>
            </div>
            <div>
              <Label>{t('wiki.tagsLabel', 'Tagy (čárkou)')}</Label>
              <Input
                size='sm'
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder={t('wiki.tagsPlaceholderUpload', 'spec, ai, mqtt')}
              />
            </div>

            {status === 'error' && (
              <Alert variant='destructive'>
                <AlertDescription>{errorMsg}</AlertDescription>
              </Alert>
            )}
          </div>
        )}
    </Modal>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────

function EditModal({
  entry,
  open,
  onClose,
  onSaved,
}: {
  entry: WikiEntry & { tags?: string[]; problem?: string; solution?: string; context?: string };
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(entry.title ?? '');
  const [module_, setModule] = useState((entry as any).module ?? '');
  const [tags, setTags] = useState(((entry as any).tags ?? []).join(', '));
  const [problem, setProblem] = useState((entry as any).problem ?? '');
  const [solution, setSolution] = useState((entry as any).solution ?? '');
  const [context, setContext] = useState((entry as any).context ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      await wikiApi.update(entry.id, {
        title,
        module: module_ as any,
        tags: tags.split(',').map((s) => s.trim()).filter(Boolean) as any,
        problem: problem as any,
        solution: solution as any,
        context: context as any,
      });
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='lg'
      title={
        <span className='inline-flex items-center gap-2'>
          <Pencil size={14} className='text-blue-400' />
          {t('wiki.editModalTitle', 'Upravit záznam')}
          <span className='font-mono text-xs text-gray-600'>{entry.id}</span>
        </span>
      }
      footer={
        <>
          <Button
            variant='primary'
            onClick={save}
            loading={saving}
            icon={<Save size={13} />}
          >
            {saving ? t('wiki.saving', 'Ukládám…') : t('wiki.save', 'Uložit')}
          </Button>
          <Button variant='secondary' onClick={onClose}>
            {t('wiki.cancel', 'Zrušit')}
          </Button>
        </>
      }
    >
      <div className='space-y-3'>
        <div>
          <Label>{t('wiki.titleLabel', 'Titulek')}</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <Label>{t('wiki.moduleLabel', 'Modul')}</Label>
            <Input
              size='sm'
              value={module_}
              onChange={(e) => setModule(e.target.value)}
              placeholder={t('wiki.modulePlaceholderEdit', 'chat, fleet, …')}
            />
          </div>
          <div>
            <Label>{t('wiki.tagsLabel', 'Tagy (čárkou)')}</Label>
            <Input
              size='sm'
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={t('wiki.tagsPlaceholderEdit', 'memory, superagent, …')}
            />
          </div>
        </div>
        <div>
          <Label>{t('wiki.problemLabel', 'Problém')}</Label>
          <Textarea size='sm' value={problem} onChange={(e) => setProblem(e.target.value)} />
        </div>
        <div>
          <Label>{t('wiki.solutionContentLabel', 'Řešení / Obsah')}</Label>
          <Textarea
            className='min-h-[12rem] font-mono text-xs'
            value={solution}
            onChange={(e) => setSolution(e.target.value)}
          />
        </div>
        <div>
          <Label>{t('wiki.contextLabel', 'Kontext')}</Label>
          <Textarea size='sm' value={context} onChange={(e) => setContext(e.target.value)} />
        </div>
        {err && (
          <Alert variant='destructive'>
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}
      </div>
    </Modal>
  );
}

// ── Delete confirm ────────────────────────────────────────────────────────────

function DeleteConfirm({
  id,
  title,
  open,
  onClose,
  onDeleted,
}: {
  id: string;
  title: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);
  const del = async () => {
    setDeleting(true);
    await wikiApi.delete(id);
    onDeleted();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      size='sm'
      title={t('wiki.deleteConfirmTitle', 'Smazat záznam?')}
      footer={
        <>
          <Button
            variant='danger'
            onClick={del}
            loading={deleting}
            icon={<Trash2 size={13} />}
          >
            {t('wiki.delete', 'Smazat')}
          </Button>
          <Button variant='secondary' onClick={onClose}>
            {t('wiki.cancel', 'Zrušit')}
          </Button>
        </>
      }
    >
      <p className='text-sm text-gray-300'>{title}</p>
      <p className='mt-1 font-mono text-[11px] text-gray-600'>{id}</p>
    </Modal>
  );
}

// ── Detail reader ─────────────────────────────────────────────────────────────

function WikiReader({
  entryId,
  onDeleted,
}: {
  entryId: string;
  onDeleted: () => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['wiki-entry', entryId],
    queryFn: async () => {
      if (!entryId.startsWith('product:')) return wikiApi.get(entryId);
      const catalog = await mcpCall<ProductDocCatalog>('acm-self.help_catalog', {
        locale: normalizeLang(i18n.language),
        audience: 'human',
        preview_edition: 'standard',
        include_content: true,
      });
      const page = catalog.pages.find((item) =>
        item.source === 'customer' && `product:${item.id}` === entryId
      );
      if (!page) throw new Error(`Wiki entry '${entryId}' not found`);
      return productDocEntry(page);
    },
  });
  const entry = data as WikiEntry | undefined;
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const handleDownload = async () => {
    if (!entry) return;
    try {
      const r = await mcpCall<{ ok: boolean; filename?: string; base64?: string; error?: string }>(
        'wiki.download',
        { id: entry.id },
      );
      if (!r.ok || !r.base64 || !r.filename) {
        throw new Error(r.error ?? t('wiki.downloadFailedError', 'Stažení selhalo'));
      }
      downloadBase64(r.base64, r.filename, 'text/markdown; charset=utf-8');
    } catch (e) {
      alert(t('wiki.downloadErrorPrefix', 'Chyba při stahování: ') + (e as Error).message);
    }
  };

  const section = (icon: React.ReactNode, label: string, content: string) => {
    if (!content) return null;
    return (
      <div className='space-y-2'>
        <div className='flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-400'>
          {icon} {label}
        </div>
        <pre className='acm-terminal whitespace-pre-wrap rounded-xl p-4 text-sm leading-relaxed'>
          {content}
        </pre>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className='flex flex-1 items-center justify-center p-10'>
        <Loader2 size={28} className='animate-spin text-blue-400' />
      </div>
    );
  }

  if (isError || !entry) {
    return (
      <div className='flex flex-1 items-center justify-center p-10'>
        <EmptyState
          icon={<AlertCircle size={32} className='mx-auto text-red-500' />}
          title={t('wiki.loadError', 'Chyba načítání wiki')}
          description={(error as Error)?.message ?? t('wiki.unknownError', 'Neznámá chyba')}
          action={
            <Button variant='secondary' onClick={() => refetch()}>
              {t('wiki.retryButton', 'Zkusit znovu')}
            </Button>
          }
        />
      </div>
    );
  }

  const isMem = MEMORY_MODULES.has((entry as any).module) || MEMORY_IDS.has(entry.id);
  const isProductDoc = entry.id.startsWith('product:');

  return (
    <>
      {!isProductDoc && <EditModal
        entry={entry as any}
        open={showEdit}
        onClose={() => setShowEdit(false)}
        onSaved={() => {
          setShowEdit(false);
          refetch();
          queryClient.invalidateQueries({ queryKey: ['wiki-entries'] });
        }}
      />}
      {!isProductDoc && <DeleteConfirm
        id={entry.id}
        title={entry.title}
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onDeleted={() => {
          queryClient.invalidateQueries({ queryKey: ['wiki-entries'] });
          onDeleted();
        }}
      />}

      <div className='flex items-start justify-between gap-3 border-b border-gray-800/70 px-5 py-4'>
        <div className='min-w-0 space-y-1.5'>
          <div className='flex flex-wrap items-center gap-2'>
            <h2 className='truncate text-lg font-bold text-white'>{entry.title}</h2>
            {isMem && <Badge variant='info'>{t('wiki.memoryTab', 'Paměť agentů')}</Badge>}
            {entry.module && <Badge variant='default'>{entry.module}</Badge>}
          </div>
          <div className='flex flex-wrap items-center gap-3 text-xs text-gray-500'>
            {entry.module && (
              <span className='inline-flex items-center gap-1'>
                <Tag size={11} /> {entry.module}
              </span>
            )}
            <span className='inline-flex items-center gap-1'>
              <Users size={11} /> {entry.author}
            </span>
            {!isProductDoc && <span className='inline-flex items-center gap-1'>
              <Clock size={11} /> {new Date(entry.created_at).toLocaleDateString('cs')}
            </span>}
          </div>
        </div>
        {!isProductDoc && <div className='flex shrink-0 flex-wrap items-center gap-1.5'>
          <Button variant='ghost' size='sm' icon={<Pencil size={12} />} onClick={() => setShowEdit(true)}>
            {t('wiki.edit', 'Upravit')}
          </Button>
          <Button variant='ghost' size='sm' icon={<Download size={12} />} onClick={handleDownload}>
            {t('wiki.download', 'Stáhnout')}
          </Button>
          <Button variant='ghost' size='sm' icon={<Trash2 size={12} />} onClick={() => setShowDelete(true)}>
            {t('wiki.delete', 'Smazat')}
          </Button>
        </div>}
      </div>

      <div className='min-h-0 flex-1 space-y-5 overflow-y-auto p-5'>
        {((entry as WikiEntry & { tags?: string[] }).tags?.length ?? 0) > 0 && (
          <div className='flex flex-wrap gap-1.5'>
            {((entry as WikiEntry & { tags?: string[] }).tags ?? []).map((tag) => (
              <Badge key={tag} variant='default'>{tag}</Badge>
            ))}
          </div>
        )}

        {section(<AlertCircle size={13} />, t('wiki.problemLabel', 'Problém'), (entry as any).problem ?? '')}
        {section(<FileText size={13} />, t('wiki.contextLabel', 'Kontext'), (entry as any).context ?? '')}
        {section(<Lightbulb size={13} />, t('wiki.solutionLabel', 'Řešení'), (entry as any).solution ?? '')}
        {(entry as any).notes?.length > 0 &&
          section(
            <StickyNote size={13} />,
            t('wiki.notesLabel', 'Poznámky'),
            ((entry as any).notes ?? []).join('\n'),
          )}
      </div>
    </>
  );
}

// ── Entry list row ────────────────────────────────────────────────────────────

function EntryRow({
  entry,
  active,
  onClick,
}: {
  entry: WikiEntry;
  active: boolean;
  onClick: () => void;
}) {
  const isMem = MEMORY_MODULES.has((entry as any).module) || MEMORY_IDS.has(entry.id);
  return (
    <button
      type='button'
      onClick={onClick}
      className={[
        'group w-full rounded-xl border px-3 py-2.5 text-left transition-colors',
        active
          ? 'border-blue-500/30 bg-blue-500/10'
          : 'border-transparent hover:border-gray-800 hover:bg-gray-800/40',
      ].join(' ')}
    >
      <div className='flex items-start gap-2.5'>
        <div
          className={[
            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            isMem ? 'bg-violet-500/10 text-violet-300' : 'bg-blue-500/10 text-blue-300',
          ].join(' ')}
        >
          {isMem ? <Brain size={14} /> : <BookOpen size={14} />}
        </div>
        <div className='min-w-0 flex-1'>
          <div className='truncate text-sm font-semibold text-gray-100 group-hover:text-white'>
            {entry.title}
          </div>
          <div className='mt-1.5 flex flex-wrap items-center gap-1'>
            {isMem && <Badge variant='info'>memory</Badge>}
            {entry.module && !isMem && <Badge variant='default'>{entry.module}</Badge>}
          </div>
          {(entry as any).problem_preview && (
            <p className='mt-1.5 line-clamp-2 text-[11px] leading-relaxed text-gray-500'>
              {(entry as any).problem_preview}
            </p>
          )}
          <div className='mt-1.5 flex flex-wrap items-center gap-2 text-[10px] text-gray-600'>
            <span>{entry.author}</span>
            <span>{new Date(entry.created_at).toLocaleDateString('cs')}</span>
            {((entry as any).tags ?? []).slice(0, 2).map((tag: string) => (
              <Badge key={tag} variant='default' className='text-[10px]'>{tag}</Badge>
            ))}
          </div>
        </div>
      </div>
    </button>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MEMORY_MODULES = new Set(['chat', 'superagent']);
const MEMORY_IDS = new Set([
  'acm-agent-memory',
  'acm-improvement-requests',
  'acm-agent-bootstrap',
  'acm-agent-quickstart',
]);

const PRODUCTION_EDITIONS = new Set(['standard', 'partner', 'plus', 'host', 'remote']);
const ALL_PRODUCTION_EDITIONS = [...PRODUCTION_EDITIONS];

function entryEditions(entry: WikiEntry): string[] {
  return Array.isArray(entry.editions) ? entry.editions : ['full'];
}

function isProductionKnowledge(entry: WikiEntry, edition: string | null): boolean {
  if ((entry.lifecycle ?? 'active') !== 'active') return false;
  const editions = entryEditions(entry);
  if (ALL_PRODUCTION_EDITIONS.every((candidate) => editions.includes(candidate))) return true;
  if (edition && PRODUCTION_EDITIONS.has(edition)) return editions.includes(edition);
  return false;
}

function isWorkingKnowledge(entry: WikiEntry): boolean {
  if ((entry.lifecycle ?? 'active') !== 'active') return true;
  return !entryEditions(entry).some((candidate) => PRODUCTION_EDITIONS.has(candidate));
}

type ViewMode = 'production' | 'working' | 'all' | 'memory';

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Wiki() {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('production');
  const searchParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : '',
  );
  const [moduleFilter, setModuleFilter] = useState<string>(searchParams.get('module') ?? '');
  const queryClient = useQueryClient();

  const locale = normalizeLang(i18n.language);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['wiki-entries', locale],
    queryFn: async () => {
      const [wikiResult, docsResult] = await Promise.allSettled([
        wikiApi.list(),
        mcpCall<ProductDocCatalog>('acm-self.help_catalog', {
          locale,
          audience: 'human',
          preview_edition: 'standard',
          include_content: false,
        }),
      ]);
      if (wikiResult.status === 'rejected' && docsResult.status === 'rejected') {
        throw wikiResult.reason;
      }
      const productEntries = docsResult.status === 'fulfilled'
        ? docsResult.value.pages.filter((page) => page.source === 'customer').map(productDocEntry)
        : [];
      const wikiEntries = wikiResult.status === 'fulfilled' ? wikiResult.value : [];
      return {
        entries: [...productEntries, ...wikiEntries],
        edition: docsResult.status === 'fulfilled'
          ? docsResult.value.actual_edition ?? docsResult.value.edition ?? null
          : null,
      };
    },
    staleTime: 30_000,
  });
  const entries = data?.entries ?? [];
  const edition = data?.edition ?? null;

  const handleUploaded = (id: string) => {
    queryClient.invalidateQueries({ queryKey: ['wiki-entries'] });
    setShowUpload(false);
    setSelected(id);
  };

  const modules = [...new Set(entries.map((e: any) => e.module).filter(Boolean))] as string[];

  const isMemory = (e: any) => MEMORY_MODULES.has(e.module) || MEMORY_IDS.has(e.id);

  const filtered = entries.filter((e: any) => {
    const matchQ = !query ||
      [e.title, e.problem_preview, e.solution, ...(e.tags ?? [])].join(' ').toLowerCase().includes(
        query.toLowerCase(),
      );
    const matchM = !moduleFilter || e.module === moduleFilter;
    const matchV = viewMode === 'all'
      ? true
      : viewMode === 'memory'
      ? isMemory(e)
      : viewMode === 'production'
      ? isProductionKnowledge(e, edition) && !isMemory(e)
      : isWorkingKnowledge(e) && !isMemory(e);
    return matchQ && matchM && matchV;
  });

  const memoryCount = entries.filter((e: any) => isMemory(e)).length;
  const productionCount = entries.filter((e) => isProductionKnowledge(e, edition) && !isMemory(e)).length;
  const workingCount = entries.filter((e) => isWorkingKnowledge(e) && !isMemory(e)).length;

  return (
    <div className='space-y-5 p-6'>
      <UploadModal
        open={showUpload}
        onClose={() => setShowUpload(false)}
        onUploaded={handleUploaded}
      />

      <PageHeader
        title={t('wiki.pageTitle', 'Wiki')}
        description={t('wiki.subtitle', '{{count}} záznamů · znalostní báze agentů a vývojářů', {
          count: entries.length,
        })}
        icon={BookOpen}
        actions={
          <>
            <Button
              variant='primary'
              size='sm'
              icon={<Upload size={14} />}
              onClick={() => setShowUpload(true)}
            >
              {t('wiki.uploadButton', 'Nahrát soubor')}
            </Button>
            <RefreshButton onRefresh={() => refetch()} size='md' />
          </>
        }
      />

      <Card>
        <CardContent className='space-y-4'>
          <SearchInput
            value={query}
            onChange={setQuery}
            size='md'
            placeholder={t('wiki.searchPlaceholder', 'Hledat...')}
            className='w-full max-w-md'
            resultCount={isLoading ? undefined : filtered.length}
            totalCount={entries.length}
          />

          <div className='flex flex-wrap items-center gap-1.5'>
            <Button
              type='button'
              size='sm'
              variant={viewMode === 'production' && !moduleFilter ? 'primary' : 'secondary'}
              icon={<BookOpen size={13} />}
              onClick={() => {
                setViewMode('production');
                setModuleFilter('');
              }}
            >
              {t('wiki.filterProduction', 'Produkční')}
              {edition && (
                <Badge variant='default' className='ml-1'>{t('wiki.edice', 'edice')} {edition}</Badge>
              )}
              <Badge
                variant={viewMode === 'production' && !moduleFilter ? 'info' : 'default'}
                className='ml-1'
              >
                {productionCount}
              </Badge>
            </Button>
            <Button
              type='button'
              size='sm'
              variant={viewMode === 'working' && !moduleFilter ? 'primary' : 'secondary'}
              icon={<FileText size={13} />}
              onClick={() => {
                setViewMode('working');
                setModuleFilter('');
              }}
            >
              {t('wiki.filterWorking', 'Pracovní / DEV')}
              <Badge
                variant={viewMode === 'working' && !moduleFilter ? 'info' : 'default'}
                className='ml-1'
              >
                {workingCount}
              </Badge>
            </Button>
            <Button
              type='button'
              size='sm'
              variant={viewMode === 'all' && !moduleFilter ? 'primary' : 'secondary'}
              icon={<BookOpen size={13} />}
              onClick={() => {
                setViewMode('all');
                setModuleFilter('');
              }}
            >
              {t('wiki.filterAll', 'Vše')}
              <Badge variant={viewMode === 'all' && !moduleFilter ? 'info' : 'default'} className='ml-1'>
                {entries.length}
              </Badge>
            </Button>
            <Button
              type='button'
              size='sm'
              variant={viewMode === 'memory' ? 'primary' : 'secondary'}
              icon={<Brain size={13} />}
              onClick={() => {
                setViewMode('memory');
                setModuleFilter('');
              }}
            >
              {t('wiki.memoryTab', 'Paměť agentů')}
              <Badge variant={viewMode === 'memory' ? 'info' : 'default'} className='ml-1'>
                {memoryCount}
              </Badge>
            </Button>
            {modules.map((m) => (
              <Button
                key={m}
                type='button'
                size='sm'
                variant={moduleFilter === m ? 'primary' : 'secondary'}
                onClick={() => {
                  setModuleFilter(m === moduleFilter ? '' : m);
                  setViewMode('all');
                }}
              >
                {m}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-[minmax(300px,380px)_1fr] lg:min-h-[62vh]'>
        <Card className='flex max-h-[70vh] flex-col lg:max-h-[calc(100vh-16rem)]'>
          <CardHeader>
            <span className='text-xs font-semibold uppercase tracking-wider text-gray-400'>
              {t('wiki.listHeader', 'Záznamy')}
            </span>
            <Badge variant='default'>{filtered.length}</Badge>
          </CardHeader>

          <div className='min-h-0 flex-1 overflow-y-auto'>
            {isError && (
              <div className='p-3'>
                <Alert variant='destructive'>
                  <AlertTitle>{t('wiki.loadError', 'Chyba načítání wiki')}</AlertTitle>
                  <AlertDescription>
                    {(error as Error)?.message ?? t('wiki.unknownError', 'Neznámá chyba')}
                  </AlertDescription>
                  <div className='mt-2'>
                    <Button variant='secondary' size='sm' onClick={() => refetch()}>
                      {t('wiki.retryButton', 'Zkusit znovu')}
                    </Button>
                  </div>
                </Alert>
              </div>
            )}

            {isLoading && (
              <div className='space-y-2 p-3'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className='h-16 animate-pulse rounded-xl bg-gray-800/40' />
                ))}
              </div>
            )}

            {!isLoading && !isError && filtered.length === 0 && (
              <EmptyState
                className='px-4 py-14'
                icon={BookOpen}
                title={query || moduleFilter
                  ? t('wiki.noResults', 'Žádné výsledky')
                  : t('wiki.emptyWiki', 'Wiki je prázdná')}
              />
            )}

            {!isLoading && !isError && filtered.length > 0 && (
              <ul className='space-y-1 p-2'>
                {filtered.map((entry: any) => (
                  <li key={entry.id}>
                    <EntryRow
                      entry={entry}
                      active={selected === entry.id}
                      onClick={() => setSelected(entry.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card className='flex max-h-[70vh] flex-col lg:max-h-[calc(100vh-16rem)]'>
          {!selected && (
            <div className='flex flex-1 items-center justify-center p-10'>
              <EmptyState
                icon={BookOpen}
                title={t('wiki.pickTitle', 'Vyber záznam')}
                description={t('wiki.pickDesc', 'Klikni na položku vlevo pro zobrazení obsahu.')}
              />
            </div>
          )}

          {selected && (
            <WikiReader
              entryId={selected}
              onDeleted={() => setSelected(null)}
            />
          )}
        </Card>
      </div>
    </div>
  );
}
